/**
 * WebSocket Context Provider
 * Provides WebSocket connection state and methods to all components.
 * Includes in-app notification sounds for real-time alerts.
 */
import React, { createContext, useContext, useEffect, useCallback, useState, useRef } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';
import { toast } from 'sonner';

// Create context
const WebSocketContext = createContext(null);

// Custom hook to use WebSocket context
export function useWS() {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWS must be used within a WebSocketProvider');
  }
  return context;
}

// Status labels for notifications
const STATUS_LABELS = {
  CREATES_TICKET: 'Erstellt', ASSIGNS_TICKET: 'Zugewiesen', ACCEPTS_JOB: 'Akzeptiert',
  DECLINES_JOB: 'Abgelehnt', PROCESSES_JOB: 'Gestartet', PAUSES_JOB: 'Unterbrochen',
  TICKET_SUCCEEDS: 'Abgeschlossen', TICKET_FAILS: 'Fehlgeschlagen',
};

// Play alert sound + vibrate (Android PWA)
let alertAudio = null;
function playAlertSound() {
  try {
    if (!alertAudio) {
      alertAudio = new Audio('/alert-sound.wav');
      alertAudio.volume = 0.5;
    }
    alertAudio.currentTime = 0;
    alertAudio.play().catch(() => {});
  } catch {}
  try {
    if ('vibrate' in navigator) {
      navigator.vibrate([200, 100, 200]);
    }
  } catch {}
}

// Provider component
export function WebSocketProvider({ children, token, userRole, userId }) {
  const [eventLog, setEventLog] = useState([]);
  
  // Grace-period refs for "Verbindung unterbrochen"-toast (suppresses brief reconnects)
  const disconnectGraceTimerRef = useRef(null);
  const disconnectToastShownRef = useRef(false);
  
  // Event handlers
  const handleEvent = useCallback((event) => {
    // Log event
    setEventLog(prev => [...prev.slice(-99), {
      id: event.event_id,
      type: event.event_type,
      timestamp: new Date().toISOString(),
      data: event
    }]);
    
    // Relay job events to dashboards: ManagerDashboard hoert darauf und laedt
    // Jobs/Users sofort nach (Live-Update statt Warten auf den 10s-Poll)
    if (event.event_type?.startsWith('job.') || event.event_type === 'completion_proof.submitted') {
      try {
        window.dispatchEvent(new CustomEvent('ws:job-event', { detail: event }));
      } catch { /* noop */ }
    }
    
    // In-App Notifications with sound
    if (event.event_type === 'job.created') {
      const job = event.payload?.job;
      if (job) {
        playAlertSound();
        toast.info('Neuer Auftrag', {
          description: job.title || 'Ein neuer Job wurde erstellt',
          duration: 5000,
        });
      }
    } else if (event.event_type === 'job.status_changed') {
      const { new_status, job_title } = event.payload || {};
      if (new_status && job_title) {
        playAlertSound();
        const label = STATUS_LABELS[new_status] || new_status;
        toast.info(`${job_title}`, {
          description: `Status: ${label}`,
          duration: 4000,
        });
      }
    } else if (event.event_type === 'job.reassigned') {
      const { job_title } = event.payload || {};
      playAlertSound();
      toast.info('Job neu zugewiesen', {
        description: job_title || '',
        duration: 5000,
      });
    } else if (event.event_type === 'completion_proof.submitted') {
      const { job_title, worker_name } = event.payload || {};
      playAlertSound();
      toast.success('Erfüllungsbestätigung eingereicht', {
        description: `${job_title || 'Job'} — bestätigt von ${worker_name || 'Worker'}`,
        duration: 6000,
      });
    } else if (event.event_type === 'notification.alert') {
      playAlertSound();
      toast.warning(event.payload?.title || 'Warnung', {
        description: event.payload?.message,
        duration: 8000,
      });
    } else if (event.event_type === 'pause.started' || event.event_type === 'pause.ended') {
      // Relay pause events to the PauseContext (no toast — the context handles UX)
      try {
        window.dispatchEvent(new CustomEvent(`ws:${event.event_type}`, { detail: event.payload }));
      } catch { /* noop */ }
    }
  }, []);
  
  const handleConnected = useCallback((data) => {
    // Clear any pending disconnect toast if the grace period has not elapsed yet,
    // or dismiss the already-shown warning if reconnect came in later.
    if (disconnectGraceTimerRef.current) {
      clearTimeout(disconnectGraceTimerRef.current);
      disconnectGraceTimerRef.current = null;
    }
    if (disconnectToastShownRef.current) {
      toast.dismiss('ws-status');
      toast.success('Verbindung wiederhergestellt', { duration: 2000 });
      disconnectToastShownRef.current = false;
    }
  }, []);
  
  const handleDisconnected = useCallback(({ code, reason }) => {
    // Filter clean shutdowns (page reload, server-initiated normal close).
    // Code 1000 = normal (also used when server replaces a stale connection),
    // 1001 = going away, 1005 = no status.
    if (code === 1000 || code === 1001 || code === 1005) return;
    
    // Grace period: only show the toast if we stay disconnected for 3 seconds.
    // Brief reconnects (<3s) happen often (Wi-Fi handoff, proxy idle, heartbeat
    // timeout) and should not surface a warning to the user.
    if (disconnectGraceTimerRef.current) return; // Already armed
    disconnectGraceTimerRef.current = setTimeout(() => {
      disconnectGraceTimerRef.current = null;
      disconnectToastShownRef.current = true;
      toast.warning('Verbindung unterbrochen', {
        id: 'ws-status',
        description: 'Versuche erneut zu verbinden...',
        duration: Infinity,
      });
    }, 3000);
  }, []);
  
  const handleError = useCallback((error) => {
    if (error.type === 'auth_failed') {
      toast.error('Sitzung abgelaufen', {
        description: 'Bitte melden Sie sich erneut an.',
      });
    }
  }, []);
  
  // Determine channels based on user role
  const getChannels = useCallback(() => {
    const channels = [];
    
    if (userId) {
      channels.push(`user:${userId}`);
    }
    
    if (userRole) {
      channels.push(`role:${userRole}`);
    }
    
    // All users subscribe to general site channel
    channels.push('site:Lager A');
    
    // Managers and admins get metrics
    if (['manager', 'admin', 'super_admin'].includes(userRole)) {
      channels.push('metrics:Lager A');
    }
    
    return channels;
  }, [userId, userRole]);
  
  // Initialize WebSocket
  const ws = useWebSocket(token, {
    onEvent: handleEvent,
    onConnected: handleConnected,
    onDisconnected: handleDisconnected,
    onError: handleError,
    autoConnect: !!token,
    channels: getChannels(),
  });
  
  // Subscribe to additional channels when connected
  // Note: Server auto-subscribes to user:{id} and role:{role} channels
  // Only subscribe once per connection, not on every state change
  const subscribedRef = React.useRef(false);
  
  useEffect(() => {
    if (ws.isConnected && !subscribedRef.current) {
      subscribedRef.current = true;
      const channels = getChannels();
      
      // Small delay to ensure connection is stable
      const timer = setTimeout(() => {
        channels.forEach(channel => {
          // Skip user and role channels - server handles these
          if (!channel.startsWith('user:') && !channel.startsWith('role:')) {
            ws.subscribe(channel);
          }
        });
      }, 500);
      
      return () => clearTimeout(timer);
    }
    
    // Reset flag when disconnected
    if (!ws.isConnected) {
      subscribedRef.current = false;
    }
  }, [ws.isConnected]); // eslint-disable-line
  
  // Context value
  const value = {
    ...ws,
    eventLog,
    clearEventLog: () => setEventLog([]),
  };
  
  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

export default WebSocketContext;
